'use client'

import React, { memo } from 'react'
import { Box, BoxProps, Theme, Typography, styled } from '@mui/material'

export enum BadgeStatus {
  NEUTRAL = 'NEUTRAL',
  SUCCESS = 'SUCCESS',
  WARNING = 'WARNING',
  DESTRUCTIVE = 'DESTRUCTIVE',
  ACTIVE = 'ACTIVE',
}

type BadgeStatusKeys = keyof typeof BadgeStatus

export type BadgeProps = {
  status: BadgeStatusKeys
  label: string
  size: 'small' | 'large'
} & BoxProps

const badgeBorderColor = (status: BadgeStatusKeys) => {
  switch (status) {
    case 'NEUTRAL':
      return 'var(--dewi_colors_dark_5)'
    case 'SUCCESS':
      return 'var(--dewi_colors_green_2)'
    case 'WARNING':
      return 'var(--dewi_colors_yellow_8)'
    case 'DESTRUCTIVE':
      return 'var(--dewi_colors_red_8)'
    case 'ACTIVE':
      return 'var(--dewi_colors_blue_8)'
  }
}

const badgeBackgroundColor = (status: BadgeStatusKeys) => {
  switch (status) {
    case 'NEUTRAL':
      return 'var(--dewi_colors_dark_6)'
    case 'SUCCESS':
      return 'var(--dewi_colors_green_5)'
    case 'WARNING':
      return 'var(--dewi_colors_yellow_5)'
    case 'DESTRUCTIVE':
      return 'var(--dewi_colors_red_5)'
    case 'ACTIVE':
      return 'var(--dewi_colors_blue_5)'
  }
}

const MainBox = styled(Box)(
  ({
    theme,
    $status,
    $size,
  }: {
    theme?: Theme
    $status: BadgeStatusKeys
    $size: 'small' | 'large'
  }) => ({
    display: 'flex',
    border: `1px solid ${badgeBorderColor($status)}`,
    background: badgeBackgroundColor($status),
    maxHeight: $size === 'small' ? 28 : 40,
    boxSizing: 'border-box',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
  }),
)

const BadgeLabel = styled(Typography)(({ isDark }: { isDark: boolean }) => ({
  fontSize: 16,
  fontWeight: 'semi-bold',
  color: isDark ? 'var(--dewi_colors_gray_9)' : 'var(--dewi_colors_gray_0)',
}))

const Badge = ({ status, label, size, ...rest }: BadgeProps) => {
  return (
    <MainBox $status={status} $size={size} {...rest}>
      <BadgeLabel isDark={status !== 'NEUTRAL'} variant="h6">
        {label}
      </BadgeLabel>
    </MainBox>
  )
}

export default memo(Badge)
